import React, { ReactNode } from "react";
import { Popover } from "@headlessui/react";
import { InformationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";

type InfoPopoverProps = {
  label: string;
  title?: string;
  children: ReactNode;
};

/** Shared info control; anchored panels stay outside scrollable containers. */
export default function InfoPopover({ label, title = `${label} help`, children }: InfoPopoverProps) {
  return (
    <Popover as="span" className="inline-flex">
      {({ close }) => (
        <>
          <Popover.Button
            aria-label={title}
            className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 md:min-h-7 md:min-w-7"
          >
            <InformationCircleIcon className="h-5 w-5" aria-hidden="true" />
          </Popover.Button>
          <Popover.Panel
            anchor={{ to: "bottom start", gap: 8, padding: 16 }}
            portal
            role="dialog"
            aria-label={title}
            className="z-50 max-h-[70vh] w-96 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-lg border border-gray-200 bg-white p-4 text-sm font-normal text-gray-700 shadow-lg"
          >
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="font-semibold text-gray-900">{title}</h3>
              <button
                type="button"
                aria-label={`Close ${label.toLowerCase()} help`}
                onClick={() => close()}
                className="rounded p-1 text-gray-500 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600"
              >
                <XMarkIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            {children}
          </Popover.Panel>
        </>
      )}
    </Popover>
  );
}
